import { Box, Flex, HStack, Heading, Text, VStack } from "@chakra-ui/react"
import { useKatex } from "@pathwright/utils/src/katex/useKatex"
import useLocalStorage from "@pathwright/utils/src/storage/useLocalStorage"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import { Indicator } from "@pathwright/ux/src/components/button/IconButton"
import { useColorModeValue } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import { getFormStateStorageKey } from "@pathwright/ux/src/components/form/state/useFormStateStorage"
import useTranslate from "@pathwright/ux/src/components/lng/useTranslate"
import OptionsMenu, {
  OptionsMenuItem
} from "@pathwright/ux/src/components/menu/OptionsMenu"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import SpaceIdentity from "@pathwright/features/src/modules/space/identity/SpaceIdentity"
import classNames from "classnames"
import moment from "moment"
import { useEffect, useMemo, useRef, useState } from "react"
import { NoteFragment, NoteVisibility } from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import RedactorContent from "../../redactor/RedactorContent"
import useNotePermissions, { NotePermissions } from "../useNotePermissions"
import { useNoteContext } from "./NoteContext"
import { getNoteFormStateKey } from "./NoteForm"
import NoteListItemDelete from "./NoteListItemDelete"

type NoteListItemProps = {
  note: NoteFragment
}

type DumbNoteItemProps = {
  note: NoteFragment
  notePermissions: Pick<
    NotePermissions,
    "canUpdate" | "canDelete" | "canViewFullAuthorName"
  >
  isDeleting?: boolean
  isExpanded?: boolean
  canTruncate?: boolean
  onToggleExpand?: () => void
  onEditNote?: () => void
  onDeleteNote?: () => void
}

export function DumbNoteItem({
  note,
  notePermissions,
  isDeleting,
  isExpanded,
  canTruncate,
  onToggleExpand,
  onEditNote,
  onDeleteNote
}: DumbNoteItemProps) {
  const { school } = usePathwrightContext()
  const { t } = useTranslate()
  const [isTruncated, setIsTruncated] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const shouldTruncate = canTruncate && isTruncated && !isExpanded

  const bgColor = useColorModeValue("white", "gray.800")
  const textColor = useColorModeValue("gray.600", "gray.300")
  const borderColor = useColorModeValue("gray.200", "gray.600")

  // Check for unsaved note form state for the "Edit note" button indicator
  const formStateStorageKey = useMemo(
    () => getFormStateStorageKey(getNoteFormStateKey(note)),
    []
  )
  const hasUnsavedChanges = useLocalStorage(formStateStorageKey)[0] != null

  // Check if content should be truncated (similar to original view logic)
  useEffect(() => {
    if (contentRef.current && canTruncate && !isExpanded) {
      const maxHeight = 86 // From original CSS
      setIsTruncated(contentRef.current.scrollHeight > maxHeight)
    }
  }, [note.body, canTruncate, isExpanded])

  useKatex(contentRef, { contentKey: note.body })

  const getAuthorName = () => {
    return notePermissions.canViewFullAuthorName
      ? note.user.full_name
      : note.user.display_name
  }

  return (
    <Box
      className={classNames("note-list-item-view", {
        truncated: shouldTruncate
      })}
      border="1px solid"
      borderColor={borderColor}
      bg={bgColor}
      borderRadius="card"
      py={2}
      px={3}
      fontSize="14px"
      w="full"
      position="relative"
      wordBreak="break-word"
      {...(shouldTruncate && { pb: "30px" })}
    >
      {/* Header with title and settings menu */}
      <Flex
        as="header"
        // Make room for settings menu.
        pr="20px"
        align="flex-start"
        justify="space-between"
      >
        <VStack align="flex-start" gap={1} flex={1}>
          <Heading as="h4" size="md" color="gray.800" m={0}>
            {note.title}
          </Heading>

          {/* Author and time */}
          <HStack gap={2} fontSize="0.8em" color={textColor}>
            {note.visibility === NoteVisibility.Everyone ||
            note.visibility === NoteVisibility.Staff ? (
              <>
                <SpaceIdentity
                  className="note-author"
                  space={school}
                  w="1.5rem"
                  h="1.5rem"
                  minW="1.5rem"
                  minH="1.5rem"
                />
                <VStack gap={0} alignItems="flex-start">
                  <Text as="span">
                    {t("notes.list.item.school_written_by", {
                      school_name: school.name
                    })}
                  </Text>
                  <Text as="span">{moment(note.created_time).fromNow()}</Text>
                </VStack>
              </>
            ) : (
              <>
                <UserAvatar
                  className="note-author"
                  size="xs"
                  user={note.user}
                />
                <VStack gap={0} alignItems="flex-start">
                  <Text as="span">
                    {t("notes.list.item.author_written_by", {
                      first_name: getAuthorName()
                    })}
                  </Text>
                  <Text as="span">{moment(note.created_time).fromNow()}</Text>
                </VStack>
              </>
            )}
          </HStack>
        </VStack>

        {/* Admin menu */}
        {(notePermissions.canUpdate || notePermissions.canDelete) && (
          <Box position="absolute" top="10px" right="18px">
            <OptionsMenu
              className="note-settings-menu"
              triggerProps={{
                size: "2xs",
                variant: "subtle",
                colorPalette: "blackAlpha"
              }}
              // triggerProps={{ "aria-label": "Note Settings", variant: "secondaryGray" }}
            >
              {notePermissions.canUpdate && onEditNote && (
                <OptionsMenuItem
                  icon="edit"
                  label={t("notes.list.item.edit")}
                  onClick={onEditNote}
                />
              )}
              {notePermissions.canDelete && onDeleteNote && (
                <OptionsMenuItem
                  icon="trash"
                  label={t("notes.list.item.delete")}
                  onClick={onDeleteNote}
                />
              )}
            </OptionsMenu>
            {/* NOTE: better UX might be to hide the indicator when the menu is open and
            render the indicator on the menu item */}
            {hasUnsavedChanges && <Indicator />}
          </Box>
        )}
      </Flex>

      {/* NoteFragment content */}
      <Box>
        <RedactorContent
          ref={contentRef}
          className="note-body brand-content"
          color={textColor}
          overflow={isExpanded ? "visible" : "hidden"}
          maxHeight={isExpanded ? "none" : "86px"}
          content={note.body}
        />

        {/* Read more indicator */}
        {shouldTruncate && onToggleExpand && (
          <Box
            className="note-read-more"
            position="absolute"
            top={0}
            bottom={1}
            left={0}
            right={0}
            textAlign="center"
            cursor="pointer"
            onClick={onToggleExpand}
          >
            <Pathicon
              icon="chevron-down"
              position="absolute"
              bottom="0px"
              left="50%"
              transform="translateX(-50%)"
              w="20px"
              h="20px"
              color="black"
            />
          </Box>
        )}
      </Box>
      {isDeleting && <NoteListItemDelete />}
    </Box>
  )
}

// Displays individual note with author info, content, and admin actions
function NoteListItem({ note }: NoteListItemProps) {
  const {
    context,
    expandedNoteIds,
    setExpandedNoteIds,
    noteSelection,
    setNoteSelection
  } = useNoteContext()
  const notePermissions = useNotePermissions(context, note)

  const isExpanded = expandedNoteIds.has(note.id)

  const handleToggleExpand = () => {
    setExpandedNoteIds((prev) => {
      const newSet = new Set(prev)
      if (isExpanded) {
        newSet.delete(note.id)
      } else {
        newSet.add(note.id)
      }
      return newSet
    })
  }

  const handleEditNote = () => {
    setNoteSelection({ action: "update", noteId: note.id })
  }

  const handleDeleteNote = () => {
    setNoteSelection({ action: "delete", noteId: note.id })
  }

  const isDeleting =
    noteSelection.action === "delete" && noteSelection.noteId === note.id

  return (
    <DumbNoteItem
      note={note}
      notePermissions={notePermissions}
      isDeleting={isDeleting}
      isExpanded={isExpanded}
      canTruncate={true}
      onToggleExpand={handleToggleExpand}
      onEditNote={handleEditNote}
      onDeleteNote={handleDeleteNote}
    />
  )
}

export default NoteListItem
